import { ArrowDown, ArrowUp, ChevronsUpDown, MoreHorizontal } from "lucide-react";
import * as React from "react";
import { Card, CardContent } from "../atoms/card.js";
import { Checkbox } from "../atoms/checkbox.js";
import { cx } from "../internal/cx.js";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "../molecules/dropdown-menu.js";
import type { HeadingGroupHeadingLevel } from "../molecules/heading-group.js";
import { TableState } from "../molecules/table-state.js";

export type DataTableAlign = "start" | "center" | "end";
export type DataTableSortDirection = "asc" | "desc";
export type DataTableVariant = "default" | "minimal" | "flush";

interface TableSortButtonProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "children"> {
  align?: DataTableAlign;
  children: React.ReactNode;
  direction?: DataTableSortDirection | "none";
}

function TableSortButton({
  align,
  children,
  className,
  direction = "none",
  ...props
}: TableSortButtonProps) {
  const Icon = direction === "asc" ? ArrowUp : direction === "desc" ? ArrowDown : ChevronsUpDown;
  return (
    <button
      className={cx(
        "cw-v2-table-sort-button",
        `cw-v2-table-sort-button--align-${align ?? "start"}`,
        `cw-v2-table-sort-button--direction-${direction}`,
        className,
      )}
      type="button"
      {...props}
    >
      <span>{children}</span>
      <Icon aria-hidden className="cw-v2-table-sort-button__icon" />
    </button>
  );
}

export interface DataTableColumn<Row> {
  /** Stable column id. Used for keys and sort callbacks. */
  id: string;
  /** Column heading shown in the table header and mobile label. */
  header: React.ReactNode;
  /** Cell render function. Keep values preformatted before they reach the table. */
  cell: (row: Row) => React.ReactNode;
  /** Horizontal alignment for the header and cells. */
  align?: DataTableAlign;
  /** Whether this column can request parent-owned sorting. */
  sortable?: boolean;
  /** Parent-owned sort direction for this column. */
  sortDirection?: DataTableSortDirection;
  /** Hide this column in the default stacked mobile layout. */
  hideOnMobile?: boolean;
  /**
   * Constrain the cell to a single line and show an ellipsis when the content
   * is wider than the column. When the cell value is a plain string, the full
   * value is also exposed via a `title` tooltip on hover. Use for free-text
   * columns (names, emails, addresses); leave off for badges, numbers, and
   * action cells. Truncation is automatically relaxed in the stacked mobile
   * layout where each cell is full-width.
   */
  truncate?: boolean;
  /** CSS grid column width, e.g. `minmax(12rem, 2fr)`. */
  width?: string;
}

export interface DataTableProps<Row>
  extends Omit<React.HTMLAttributes<HTMLDivElement>, "children"> {
  /** Column definitions. Keep ids stable across renders. */
  columns: Array<DataTableColumn<Row>>;
  /** Rows to render. Filtering, pagination, and fetching stay in the parent. */
  rows: Row[];
  /** Stable row id builder. Prefer domain ids over array indices. */
  getRowId: (row: Row, index: number) => string;
  /** Short context rendered above the table grid. Prefer card headings for section titles. */
  caption?: React.ReactNode;
  /** Visual density. Use `minimal` for simple one-line rows; `flush` for a unified bordered table. */
  variant?: DataTableVariant;
  /** Custom empty state. Defaults to `TableState`. */
  emptyState?: React.ReactNode;
  /** Whether to render the loading state instead of rows. */
  loading?: boolean;
  /** Custom loading state. Defaults to loading `TableState`. */
  loadingState?: React.ReactNode;
  /** Parent-owned selected row ids. */
  selectedRowIds?: string[];
  /** Enables row selection and reports individual row checkbox changes. */
  onRowSelect?: (rowId: string, selected: boolean) => void;
  /** Reports header checkbox changes. Omit to disable select-all while keeping row selection. */
  onSelectAll?: (selected: boolean) => void;
  /** Reports sortable header activation. Parent owns the resulting sort state. */
  onSortChange?: (columnId: string) => void;
  /** Optional custom mobile card renderer. When set, mobile hides the table grid and renders cards. */
  mobileCardRender?: (row: Row) => React.ReactNode;
  /**
   * Makes each body row activate as a whole (e.g. navigate to a detail page).
   * When set, rows render with `role="button"`, keyboard support (Enter/Space),
   * a pointer cursor, and an accent hover — and fire this callback on click or
   * key activation. Clicks inside the selection or row-actions cells are ignored
   * so those controls stay independent, and a click that merely ends a text
   * selection does not activate the row. Prefer this over wrapping a single cell
   * in a link: the whole row becomes the target and the affordance is consistent.
   */
  onRowActivate?: (row: Row, rowId: string) => void;
  /** Builds the accessible name for each activatable row. Default: `Open row {rowId}`. */
  rowActivateAriaLabel?: (row: Row, rowId: string) => string;
  /** Full CSS grid-template-columns override. */
  gridTemplateColumns?: string;
  /** Builds the accessible name for each row selection checkbox. */
  rowSelectionAriaLabel?: (row: Row, rowId: string) => string;
  /**
   * When set, appends a trailing column with a kebab-button + dropdown.
   * Return the menu body (typically `DropdownMenuItem` nodes).
   */
  rowActions?: (row: Row) => React.ReactNode;
  /** Visible header label for the actions column. Default empty. */
  rowActionsHeader?: React.ReactNode;
  /** `aria-label` on the actions column header for screen readers. Default `"Actions"`. */
  rowActionsAriaLabel?: string;
  /** Builds the accessible name for each kebab trigger. Default: `${rowActionsAriaLabel}, {rowId}`. */
  rowActionsTriggerAriaLabel?: (row: Row, rowId: string) => string;
  /** Grid width token for the actions column when `rowActions` is set. Default `2.5rem`. */
  rowActionsColumnWidth?: string;
}

export interface DataTableCardProps
  extends Omit<React.HTMLAttributes<HTMLElement>, "children" | "title"> {
  /** Card heading. Choose the heading level from the surrounding page structure. */
  title: React.ReactNode;
  /** Semantic heading level. Usually `2` for page sections or `3` inside larger sections. */
  headingLevel?: HeadingGroupHeadingLevel;
  /** Optional supporting copy under the card heading. */
  description?: React.ReactNode;
  /** Heading group metadata/status slot. */
  meta?: React.ReactNode;
  /** Optional small label above the title. */
  eyebrow?: React.ReactNode;
  /** Optional header actions, usually export/add controls. */
  actions?: React.ReactNode;
  /** Optional filter control rendered above the rows when results are filtered. */
  filters?: React.ReactNode;
  /** The `DataTable` instance for this card. */
  children: React.ReactNode;
}

export function DataTable<Row>({
  caption,
  className,
  columns,
  emptyState,
  getRowId,
  gridTemplateColumns,
  loading = false,
  loadingState,
  mobileCardRender,
  onRowActivate,
  onRowSelect,
  onSelectAll,
  onSortChange,
  rowActivateAriaLabel,
  rowSelectionAriaLabel,
  rowActions,
  rowActionsAriaLabel = "Actions",
  rowActionsColumnWidth = "2.5rem",
  rowActionsHeader,
  rowActionsTriggerAriaLabel,
  rows,
  selectedRowIds = [],
  variant = "default",
  ...props
}: DataTableProps<Row>) {
  const selectedSet = React.useMemo(() => new Set(selectedRowIds), [selectedRowIds]);
  const rowIds = rows.map(getRowId);
  const hasSelection = Boolean(onRowSelect);
  const renderRowActions = rowActions;
  const hasRowActions = renderRowActions != null;
  const allSelected = rowIds.length > 0 && rowIds.every((id) => selectedSet.has(id));
  const someSelected = rowIds.some((id) => selectedSet.has(id));
  const selectAllState = someSelected && !allSelected ? "indeterminate" : allSelected;
  const columnTemplate =
    gridTemplateColumns ??
    [
      hasSelection ? "2rem" : null,
      ...columns.map((column) => column.width ?? "minmax(7rem, 1fr)"),
      hasRowActions ? rowActionsColumnWidth : null,
    ]
      .filter(Boolean)
      .join(" ");
  const gridStyle = { "--cw-v2-data-table-columns": columnTemplate } as React.CSSProperties;

  const visibleRows = !loading && rows.length > 0;
  const resolvedLoadingState = loadingState ?? (
    <TableState variant="loading" title="Loading table rows" />
  );
  const resolvedEmptyState = emptyState ?? <TableState title="No results found" />;

  return (
    <div
      className={cx(
        "cw-v2-data-table",
        `cw-v2-data-table--variant-${variant}`,
        mobileCardRender && "cw-v2-data-table--has-mobile-cards",
        className,
      )}
      {...props}
    >
      {caption ? <div className="cw-v2-data-table__caption">{caption}</div> : null}
      <div className="cw-v2-data-table__grid" role="table">
        <div className="cw-v2-data-table__header" role="row" style={gridStyle}>
          {hasSelection ? (
            <div className="cw-v2-data-table__select-cell" role="columnheader">
              <Checkbox
                checked={selectAllState}
                aria-label="Select all rows"
                disabled={!onSelectAll}
                onCheckedChange={(checked) => onSelectAll?.(checked === true)}
              />
            </div>
          ) : null}
          {columns.map((column) => (
            <div
              key={column.id}
              className={cx(
                "cw-v2-data-table__header-cell",
                `cw-v2-data-table__cell--align-${column.align ?? "start"}`,
                column.hideOnMobile && "cw-v2-data-table__cell--mobile-hidden",
              )}
              role="columnheader"
              aria-sort={
                column.sortDirection === "asc"
                  ? "ascending"
                  : column.sortDirection === "desc"
                    ? "descending"
                    : undefined
              }
            >
              {column.sortable && onSortChange ? (
                <TableSortButton
                  align={column.align ?? "start"}
                  direction={column.sortDirection ?? "none"}
                  onClick={() => onSortChange?.(column.id)}
                >
                  {column.header}
                </TableSortButton>
              ) : (
                column.header
              )}
            </div>
          ))}
          {hasRowActions ? (
            <div
              className={cx(
                "cw-v2-data-table__header-cell",
                "cw-v2-data-table__header-cell--row-actions",
                "cw-v2-data-table__cell--align-end",
              )}
              role="columnheader"
            >
              {/* Visible label when provided; otherwise a visually-hidden label so
                  axe's empty-table-header rule passes. aria-label alone is not
                  enough. */}
              {rowActionsHeader != null && rowActionsHeader !== "" ? (
                rowActionsHeader
              ) : (
                <span
                  style={{
                    border: 0,
                    clip: "rect(0 0 0 0)",
                    height: 1,
                    margin: -1,
                    overflow: "hidden",
                    padding: 0,
                    position: "absolute",
                    width: 1,
                  }}
                >
                  {rowActionsAriaLabel}
                </span>
              )}
            </div>
          ) : null}
        </div>

        <div className="cw-v2-data-table__body" role="rowgroup">
          {loading ? (
            <div className="cw-v2-data-table__state" role="row">
              <div className="cw-v2-data-table__state-cell" role="cell">
                {resolvedLoadingState}
              </div>
            </div>
          ) : rows.length === 0 ? (
            <div className="cw-v2-data-table__state" role="row">
              <div className="cw-v2-data-table__state-cell" role="cell">
                {resolvedEmptyState}
              </div>
            </div>
          ) : (
            rows.map((row, rowIndex) => {
              const rowId = getRowId(row, rowIndex);
              const interactive = Boolean(onRowActivate);
              // A click that merely ends a text selection inside the row must not
              // navigate; and clicks originating in the selection / row-actions
              // cells are those controls' own, not a row activation.
              const activateFromEvent = (target: EventTarget | null) => {
                if (!onRowActivate) return;
                if (
                  target instanceof Element &&
                  target.closest(
                    ".cw-v2-data-table__cell--row-actions, .cw-v2-data-table__select-cell",
                  )
                ) {
                  return;
                }
                if (typeof window !== "undefined" && window.getSelection()?.toString()) return;
                onRowActivate(row, rowId);
              };
              return (
                <div
                  key={rowId}
                  className={cx(
                    "cw-v2-data-table__row",
                    selectedSet.has(rowId) && "cw-v2-data-table__row--selected",
                    interactive && "cw-v2-data-table__row--interactive",
                  )}
                  // Stays role="row" to keep the table's ARIA structure intact
                  // (a role=button child of a rowgroup is an aria-required-children
                  // violation). Interactive rows become focusable with a button-like
                  // activation contract via aria-keyshortcuts + click/key handlers.
                  role="row"
                  tabIndex={interactive ? 0 : undefined}
                  aria-label={
                    interactive
                      ? (rowActivateAriaLabel?.(row, rowId) ?? `Open row ${rowId}`)
                      : undefined
                  }
                  aria-keyshortcuts={interactive ? "Enter Space" : undefined}
                  aria-selected={hasSelection ? selectedSet.has(rowId) : undefined}
                  style={gridStyle}
                  onClick={interactive ? (event) => activateFromEvent(event.target) : undefined}
                  onKeyDown={
                    interactive
                      ? (event) => {
                          if (event.key === "Enter" || event.key === " ") {
                            event.preventDefault();
                            activateFromEvent(event.target);
                          }
                        }
                      : undefined
                  }
                >
                  {hasSelection ? (
                    <div className="cw-v2-data-table__select-cell" role="cell">
                      <Checkbox
                        checked={selectedSet.has(rowId)}
                        aria-label={rowSelectionAriaLabel?.(row, rowId) ?? `Select row ${rowId}`}
                        onCheckedChange={(checked) => onRowSelect?.(rowId, checked === true)}
                      />
                    </div>
                  ) : null}
                  {columns.map((column) => {
                    const value = column.cell(row);
                    // When truncating, expose the full value as a native tooltip
                    // — but only when it's a plain string; arbitrary nodes can't
                    // be a title and may already render their own affordances.
                    const cellTitle =
                      column.truncate && typeof value === "string" ? value : undefined;
                    return (
                      <div
                        key={column.id}
                        className={cx(
                          "cw-v2-data-table__cell",
                          `cw-v2-data-table__cell--align-${column.align ?? "start"}`,
                          column.hideOnMobile && "cw-v2-data-table__cell--mobile-hidden",
                          column.truncate && "cw-v2-data-table__cell--truncate",
                        )}
                        role="cell"
                      >
                        <span className="cw-v2-data-table__mobile-label">{column.header}</span>
                        <span className="cw-v2-data-table__cell-value" title={cellTitle}>
                          {value}
                        </span>
                      </div>
                    );
                  })}
                  {hasRowActions ? (
                    <div
                      className={cx(
                        "cw-v2-data-table__cell",
                        "cw-v2-data-table__cell--row-actions",
                        "cw-v2-data-table__cell--align-end",
                      )}
                      role="cell"
                      onClick={(event) => event.stopPropagation()}
                      onKeyDown={(event) => event.stopPropagation()}
                    >
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button
                            type="button"
                            aria-label={
                              rowActionsTriggerAriaLabel?.(row, rowId) ??
                              `${rowActionsAriaLabel}, ${rowId}`
                            }
                            className="cw-v2-button cw-v2-icon-button cw-v2-button--ghost cw-v2-icon-button--sm cw-v2-data-table__row-actions-trigger"
                          >
                            <span className="cw-v2-button__icon">
                              <MoreHorizontal aria-hidden size={18} />
                            </span>
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {renderRowActions(row)}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  ) : null}
                </div>
              );
            })
          )}
        </div>
      </div>

      {mobileCardRender && visibleRows ? (
        <div className="cw-v2-data-table__mobile-cards">
          {rows.map((row, rowIndex) => (
            <div key={getRowId(row, rowIndex)} className="cw-v2-data-table__mobile-card">
              {mobileCardRender(row)}
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

DataTable.displayName = "DataTable";

/**
 * Canonical card composition for data tables.
 *
 * Use `filters` for `FilterTagsBar` so the visual order stays:
 * heading, filters, table. `DataTable` remains presentational; parents still
 * own sort, selection, pagination, filters, and fetch lifecycle state.
 */
export const DataTableCard = React.forwardRef<HTMLElement, DataTableCardProps>(
  function DataTableCard(
    {
      actions,
      children,
      className,
      description,
      eyebrow,
      filters,
      headingLevel = 3,
      meta,
      title,
      ...props
    },
    ref,
  ) {
    const Heading = `h${headingLevel}` as keyof React.JSX.IntrinsicElements;

    return (
      <Card
        ref={ref}
        className={cx("cw-v2-data-table-shell", "cw-v2-data-table-card", className)}
        {...props}
      >
        <CardContent className="cw-v2-data-table-shell__content cw-v2-data-table-card__content">
          <div className="cw-v2-data-table-shell__header cw-v2-data-table-card__header">
            {eyebrow ? <div className="cw-v2-data-table-card__eyebrow">{eyebrow}</div> : null}
            <div className="cw-v2-data-table-card__title-row">
              <Heading className="cw-v2-data-table-shell__title">{title}</Heading>
              {meta ? <div className="cw-v2-data-table-card__meta">{meta}</div> : null}
              {actions ? <div className="cw-v2-data-table-card__actions">{actions}</div> : null}
            </div>
            {description ? (
              <p className="cw-v2-data-table-shell__description">{description}</p>
            ) : null}
          </div>
          {filters ? <div className="cw-v2-data-table-card__filters">{filters}</div> : null}
          <div className="cw-v2-data-table-card__table">{children}</div>
        </CardContent>
      </Card>
    );
  },
);
DataTableCard.displayName = "DataTableCard";
